import { useState, type ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { Badge } from '@/app/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';

/** H3PAY-D9 API codes for an approver's own time, expense or per diem. */
export const SELF_APPROVAL_FORBIDDEN = 'self_approval_forbidden';
export const SELF_APPROVAL_CONFIRMATION_REQUIRED = 'self_approval_confirmation_required';

function responseData(
  error: unknown,
): { error?: unknown; code?: unknown; detail?: unknown; record_label?: unknown } | null {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data;
  return data && typeof data === 'object' ? data : null;
}

export function approvalErrorCode(error: unknown): string | null {
  const code = responseData(error)?.code;
  return typeof code === 'string' ? code : null;
}

function approvalRecordLabel(error: unknown): string | null {
  const label = responseData(error)?.record_label;
  return typeof label === 'string' && label ? label : null;
}

export function approvalErrorMessage(error: unknown, fallback: string): string {
  const data = responseData(error);
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.detail === 'string') return data.detail;
  return fallback;
}

export function selfApprovalWarning(recordLabel: string): string {
  return `You're approving your own ${recordLabel}. This will be marked Self-approved and logged.`;
}

export function SelfApprovedBadge({ selfApproved }: { selfApproved?: boolean }) {
  if (!selfApproved) return null;
  return (
    <Badge className="bg-amber-100 text-amber-800" title="Approved by the worker's own account">
      Self-approved
    </Badge>
  );
}

export function ApprovalError({ message, className }: { message: string | null; className?: string }) {
  if (!message) return null;
  return (
    <p role="alert" className={`text-sm text-red-700 ${className ?? ''}`}>
      {message}
    </p>
  );
}

type Send = (confirmSelfApproval: boolean) => Promise<unknown>;
type Key = number | string;

/**
 * Approve flow for records the approver may own. The API decides: a Manager
 * on their own record gets a 403 message shown in red; an Admin (or a Manager
 * when the company allows it) gets the Self-approved warning, and the request
 * is re-sent with confirm_self_approval only after they confirm.
 */
export function useSelfApproval(recordLabel: string) {
  const [error, setError] = useState<{ key: Key; message: string } | null>(null);
  const [pending, setPending] = useState<{ key: Key; send: Send; label: string } | null>(null);
  const [confirming, setConfirming] = useState(false);

  const fail = (key: Key, err: unknown) =>
    setError({ key, message: approvalErrorMessage(err, `Failed to approve ${recordLabel}.`) });

  const requestConfirmation = (key: Key, send: Send, label: string = recordLabel) =>
    setPending({ key, send, label });

  const approve = async (key: Key, send: Send) => {
    setError(null);
    try {
      await send(false);
    } catch (err) {
      if (approvalErrorCode(err) === SELF_APPROVAL_CONFIRMATION_REQUIRED) {
        requestConfirmation(key, send, approvalRecordLabel(err) ?? recordLabel);
      } else {
        fail(key, err);
      }
    }
  };

  const confirm = async () => {
    if (!pending) return;
    setConfirming(true);
    try {
      await pending.send(true);
    } catch (err) {
      fail(pending.key, err);
    } finally {
      setConfirming(false);
      setPending(null);
    }
  };

  const dialogLabel = pending?.label ?? recordLabel;
  const dialog: ReactNode = (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open && !confirming) setPending(null);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Approve your own {dialogLabel}?</AlertDialogTitle>
          <AlertDialogDescription>{selfApprovalWarning(dialogLabel)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={confirming}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              void confirm();
            }}
            disabled={confirming}
          >
            {confirming ? 'Approving...' : 'Approve as self-approved'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return {
    approve,
    requestConfirmation,
    setError: (key: Key, message: string | null) => setError(message ? { key, message } : null),
    errorFor: (key: Key) => (error?.key === key ? error.message : null),
    errorMessage: error?.message ?? null,
    dialog,
  };
}
